import styled from 'styled-components';

export const Page = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  margin: 24px 0;
  color: ${({ theme }) => theme.palette[850]};
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

export const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette[900]};
`;

export const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.palette[650]};
  font-size: 13px;
  line-height: 1.6;
  overflow-wrap: anywhere;
`;

export const Action = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-height: 38px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid ${({ theme, $primary, $danger }) =>
    $danger ? theme.errors[600] : $primary ? theme.primary[500] : theme.palette[400]};
  background: ${({ theme, $primary }) => $primary ? theme.primary[500] : theme.palette[10]};
  color: ${({ theme, $primary, $danger }) =>
    $primary ? '#fff' : $danger ? theme.errors[600] : theme.palette[850]};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover:not(:disabled) { filter: brightness(0.95); }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary[400]};
    outline-offset: 2px;
  }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const Filter = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 14px;
  cursor: pointer;
  input {
    width: 16px;
    height: 16px;
    accent-color: ${({ theme }) => theme.primary[500]};
  }
`;

export const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.palette[350]};
  border-radius: 12px;
  background: ${({ theme }) => theme.palette[10]};
  padding: 24px;
`;

export const Empty = styled(Panel)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 48px 24px;
`;

export const TableScroll = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.palette[350]};
  border-radius: 12px;
`;

export const Table = styled.table`
  width: 100%;
  min-width: 740px;
  border-collapse: collapse;
  text-align: left;
  font-size: 13px;
  background: ${({ theme }) => theme.palette[10]};
  th {
    background: ${({ theme }) => theme.palette[200]};
    color: ${({ theme }) => theme.palette[650]};
    font-weight: 500;
    white-space: nowrap;
  }
  th, td { padding: 14px 16px; vertical-align: middle; }
  td { border-top: 1px solid ${({ theme }) => theme.palette[350]}; }
  td:first-child { min-width: 130px; max-width: 280px; overflow-wrap: anywhere; }
  td:nth-child(2) { white-space: nowrap; }
  td:last-child { text-align: right; }
`;

export const Code = styled.code`
  font-family: ui-monospace, monospace;
  font-size: 12px;
  overflow-wrap: anywhere;
`;

export const ErrorText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.isDark ? theme.errors[200] : theme.errors[600]};
  font-size: 13px;
  line-height: 1.5;
`;

export const Usage = styled(Panel)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 16px 20px;
  a {
    color: ${({ theme }) => theme.isDark ? theme.primary[300] : theme.primary[500]};
    font-size: 13px;
  }
`;

export const Dialog = styled.dialog`
  position: fixed;
  inset: 0;
  width: min(480px, calc(100% - 32px));
  max-height: calc(100% - 48px);
  margin: auto;
  padding: 24px;
  box-sizing: border-box;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.palette[400]};
  border-radius: 14px;
  background: ${({ theme }) => theme.palette[10]};
  color: ${({ theme }) => theme.palette[850]};
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.2);
  &::backdrop { background: rgba(0, 0, 0, 0.55); }
`;

export const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
`;

export const Field = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.palette[400]};
  border-radius: 6px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.palette[10]};
  color: ${({ theme }) => theme.palette[900]};
  font-size: 14px;
  &:focus { outline: 2px solid ${({ theme }) => theme.primary[400]}; outline-offset: 1px; }
`;

export const Secret = styled.textarea`
  width: 100%;
  min-height: 100px;
  box-sizing: border-box;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.primary[400]};
  border-radius: 8px;
  background: ${({ theme }) => theme.palette[200]};
  color: ${({ theme }) => theme.palette[900]};
  font-family: ui-monospace, monospace;
  font-size: 13px;
  overflow-wrap: anywhere;
  resize: vertical;
`;
